import { createFileRoute, Link } from "@tanstack/react-router";

import { LegalCallout, LegalDocument, LegalSection } from "@/features/legal/LegalDocument";
import { LEGAL_RESPONSIBLE } from "@/features/legal/legal-config";

export const Route = createFileRoute("/legal/licencia")({
  head: () => ({
    meta: [
      { title: "Condiciones de descarga, licencia y actualizaciones | Socrattica" },
      {
        name: "description",
        content:
          "Licencia personal, descargas, compatibilidad y actualizaciones de productos digitales Socrattica.",
      },
    ],
  }),
  component: LicensePage,
});

function LicensePage() {
  return (
    <LegalDocument
      eyebrow="Descarga, licencia y actualizaciones"
      title="Compras acceso; la autoría permanece."
      summary="Estas condiciones explican cómo descargar, guardar y usar los productos digitales, qué usos están prohibidos y cuándo una actualización está incluida."
    >
      <LegalCallout title="Licencia en una frase">
        <p>
          Puedes usar el recurso para tu aprendizaje personal y hacer copias de respaldo razonables;
          no puedes revenderlo, compartirlo, publicarlo ni convertirlo en material para un servicio
          comercial.
        </p>
      </LegalCallout>

      <LegalSection id="otorgamiento" title="1. Licencia otorgada">
        <p>
          Con el pago aprobado, Socrattica te concede una licencia personal, limitada, no exclusiva,
          no transferible y no sublicenciable para descargar y usar el producto conforme a su ficha.
          La licencia no transfiere la propiedad intelectual del archivo, diseño, texto, audio,
          ejercicios, marca o metodología.
        </p>
        <p>
          Salvo que la ficha indique una duración, la licencia de la versión comprada es indefinida,
          sujeta al cumplimiento de estas condiciones.
        </p>
      </LegalSection>

      <LegalSection id="permitido" title="2. Usos permitidos">
        <ul>
          <li>Leer, escuchar o completar el material para fines personales y no comerciales.</li>
          <li>Descargarlo en tus dispositivos de uso personal.</li>
          <li>Imprimir una copia razonable para tu propio estudio cuando el formato lo permita.</li>
          <li>Crear una copia privada de respaldo sin eliminar avisos de autoría.</li>
          <li>
            Citar fragmentos breves conforme a la ley, con atribución y sin sustituir la obra.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="prohibido" title="3. Usos no permitidos">
        <ul>
          <li>
            Revender, regalar, prestar, sublicenciar o compartir el archivo o enlace de acceso.
          </li>
          <li>
            Publicarlo en redes, nubes compartidas, grupos, repositorios o plataformas educativas.
          </li>
          <li>
            Reproducirlo para alumnos, clientes, empleados o miembros de una organización sin una
            licencia expresa para ese alcance.
          </li>
          <li>
            Modificarlo para atribuírtelo, crear una copia sustancial o retirar marcas y créditos.
          </li>
          <li>
            Usarlo para entrenar, alimentar o evaluar sistemas automatizados sin autorización.
          </li>
          <li>
            Eludir controles de acceso, automatizar descargas o distribuir enlaces temporales.
          </li>
        </ul>
        <p>
          Si necesitas una licencia para aula, equipo u organización, escribe a{" "}
          <a href={`mailto:${LEGAL_RESPONSIBLE.email}`}>{LEGAL_RESPONSIBLE.email}</a> antes de usar
          el recurso.
        </p>
      </LegalSection>

      <LegalSection id="descarga" title="4. Acceso y descarga">
        <p>
          Inicia sesión con el correo verificado asociado al pedido. Socrattica comprueba que la
          compra esté aprobada y muestra un botón privado, sin publicar la ubicación permanente del
          archivo. El botón puede utilizarse nuevamente desde tu cuenta mientras la compra
          permanezca aprobada y el archivo siga disponible para esa versión.
        </p>
        <p>
          Te recomendamos conservar una copia local y un respaldo privado. Si retiramos un producto
          de la venta, procuraremos mantener la versión adquirida para sus compradores o brindar una
          solución equivalente. Un reembolso, contracargo o anulación válida sí deshabilita nuevas
          descargas de esa compra.
        </p>
      </LegalSection>

      <LegalSection id="compatibilidad" title="5. Formato y compatibilidad">
        <p>
          La ficha identifica formatos como PDF, EPUB o PDF + Audio. Antes de comprar verifica que
          tu dispositivo tenga un lector compatible, espacio disponible y conexión para la descarga.
          No entregamos una aplicación o dispositivo salvo indicación expresa.
        </p>
      </LegalSection>

      <LegalSection id="actualizaciones" title="6. Correcciones y actualizaciones">
        <p>
          La compra incluye la versión descrita en la fecha del pedido. Las correcciones técnicas o
          de erratas de esa versión pueden entregarse sin costo. Nuevas ediciones, módulos,
          traducciones, audios adicionales o cambios sustanciales no están incluidos salvo que la
          ficha o promoción lo diga expresamente.
        </p>
        <p>
          No prometemos una frecuencia de actualización. Si una actualización cambia requisitos de
          compatibilidad, procuraremos informar los cambios relevantes.
        </p>
      </LegalSection>

      <LegalSection id="proteccion" title="7. Protección del archivo">
        <p>
          Los productos pueden contener identificadores de pedido, marcas de agua u otras medidas
          razonables para proteger la distribución, siempre respetando la{" "}
          <Link to="/legal/privacidad">política de privacidad</Link>. No debes retirar ni alterar
          esas medidas.
        </p>
      </LegalSection>

      <LegalSection id="incumplimiento" title="8. Incumplimiento">
        <p>
          Una distribución no autorizada puede causar suspensión de acceso, terminación de la
          licencia y acciones para proteger derechos de autor. Antes de una medida permanente,
          procuraremos verificar los hechos y ofrecer un canal de aclaración, excepto cuando sea
          urgente contener fraude o riesgo de seguridad.
        </p>
      </LegalSection>

      <LegalSection id="problemas" title="9. Problemas con el archivo">
        <p>
          Si el archivo está corrupto, incompleto o no corresponde a lo comprado, no necesitas
          volver a pagar. Abre un <Link to="/productos/tickets">ticket</Link> para recibir una copia
          funcional, corrección, reemplazo o la solución prevista en la{" "}
          <Link to="/legal/reembolsos">política de reembolsos</Link>.
        </p>
      </LegalSection>
    </LegalDocument>
  );
}
